/**
 * @file web/src/utils/seo/canonical.ts
 * @description Canonical URL helper (spec §16.4.6).
 *
 * Builds the `<link rel="canonical">` URL for the current view. The
 * canonical includes only the query parameters that materially change
 * the result set (caliber, brand, useCases, etc.) — UTM/sort/page
 * parameters are stripped to avoid duplicate-content traps.
 */

const PUBLIC_ORIGIN =
  // The VITE_PUBLIC_ORIGIN env var is set at build time; falls back to
  // the runtime origin when missing (e.g. local dev).
  import.meta.env.VITE_PUBLIC_ORIGIN ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://roundrecon.com');

/** Query params that DO appear in canonical URLs (material to result set). */
const CANONICAL_QUERY_KEYS = new Set([
  'q',
  'caliber',
  'ammoType',
  'brand',
  'bulletType',
  'caseMaterial',
  'useCases',
  'retailerId',
  'inStockOnly',
  'excludeRemanufactured',
  'minPricePerRound',
  'maxPricePerRound',
  'minRoundCount',
  'lotNumber',
]);

/**
 * Build the canonical URL for a path + filter state.
 *
 * @param path  Pathname (e.g. "/search" or "/products/federal-hst-9mm-124gr-jhp")
 * @param search Current URLSearchParams (or query string) — only the
 *               keys in CANONICAL_QUERY_KEYS survive.
 * @returns canonical absolute URL string
 */
export function buildCanonicalUrl(path: string, search?: URLSearchParams | string): string {
  const params = parseSearch(search);
  const filtered = new URLSearchParams();
  // Sort keys + values to keep the canonical deterministic across re-renders.
  const keys = [...params.keys()].filter((k) => CANONICAL_QUERY_KEYS.has(k)).sort();
  for (const k of keys) {
    const values = params.getAll(k).sort();
    for (const v of values) filtered.append(k, v);
  }
  const qs = filtered.toString();
  return `${PUBLIC_ORIGIN}${path}${qs ? `?${qs}` : ''}`;
}

/**
 * @param search URLSearchParams or query string (with or without "?")
 * @returns parsed URLSearchParams
 */
function parseSearch(search?: URLSearchParams | string): URLSearchParams {
  if (!search) return new URLSearchParams();
  if (typeof search === 'string') return new URLSearchParams(search.replace(/^\?/, ''));
  return search;
}
